/**
 * The guest demo's first-run "How to play" step (`q-demo-touch-first-run-onboarding`, Owner Desk
 * #279; docs/design/guest-demo.md §5.7.4). Pure: what the step says and whether it shows, so the
 * rules are unit-tested with no DOM.
 *
 * Why it exists: an 8-year-old tester on a phone "took one look at the mobile version and asked
 * what to do". The just-in-time hints (AD-844, §5.7.3) arrive one short line at a time, after the
 * player has already stalled, and nothing anywhere offered the on-screen button controls (they sit
 * behind Settings, default off, `mobile-layout.md` §4). So the first entry now opens with one
 * card: how to move, how to look, how to use things, how to fight, where the first task is, and,
 * on a touch device, an early offer of the on-screen controls.
 *
 * Rules:
 * 1. **Once per browser.** Shown on the first entry; closing it records it (localStorage,
 *    {@link HOW_TO_PLAY_KEY}). Storage refused (private mode) ⇒ it shows once per page load.
 * 2. **The words follow the input**: tap / drag / pinch on touch, keys and mouse on desktop.
 * 3. **The controls offer is touch-only and asked once.** It appears when the player has never
 *    chosen the on-screen-controls setting either way (`mobileControlsChosen`); the answer is the
 *    same setting Settings writes, so it is remembered and can be changed there later.
 *
 * Copy lives here, not in content/, for the reason `ui/hints/hintCatalog.ts` gives: it is HUD
 * chrome tied to the client's own controls, not world content (a content category would be a
 * grammar change, AD-100). No tuning number appears (AD-321).
 */

import { DEMO_GUIDE_NAME } from '../hints/hintCatalog.js';
import type { HintInput } from '../hints/hintEngine.js';

/** localStorage key: present ⇒ this browser has been through the step. */
export const HOW_TO_PLAY_KEY = 'gc.howToPlay.v1';

export interface HowToPlayRow {
  /** Stable id (the lane and the tests find rows by it). */
  id: 'move' | 'look' | 'use' | 'fight' | 'task';
  /** A short label a child can read at a glance. */
  label: string;
  text: string;
}

export interface HowToPlayPlan {
  input: HintInput;
  title: string;
  rows: HowToPlayRow[];
  /** Offer the on-screen joystick + buttons (touch, never chosen before). */
  offerControls: boolean;
  /** The one button that closes the step. */
  goLabel: string;
}

/** The offer's words and its two answers (the lane taps these texts). */
export const CONTROLS_OFFER = {
  question: 'Want a joystick and buttons on the screen?',
  yes: 'Yes, show them',
  no: 'No thanks',
  later: 'You can change this any time in Menu → Settings.',
} as const;

export const HOW_TO_PLAY_GO = "Let's go";

const ROWS: Record<HintInput, readonly HowToPlayRow[]> = {
  touch: [
    { id: 'move', label: 'Move', text: 'Tap the ground to walk there.' },
    { id: 'look', label: 'Look', text: 'Drag with one finger to look around. Pinch to zoom.' },
    {
      id: 'use',
      label: 'Do things',
      text: 'When a button like Talk or Gather pops up at the bottom, tap it.',
    },
    { id: 'fight', label: 'Fight', text: 'Tap a creature to attack it.' },
    {
      id: 'task',
      label: 'Your first task',
      text: `Talk to ${DEMO_GUIDE_NAME}. She is close by: the gold ◆ on the Map.`,
    },
  ],
  desktop: [
    { id: 'move', label: 'Move', text: 'WASD, or click the ground to walk there.' },
    { id: 'look', label: 'Look', text: 'Hold the right mouse button and drag. Scroll to zoom.' },
    {
      id: 'use',
      label: 'Do things',
      text: 'When a prompt like Talk or Gather appears at the bottom, press E.',
    },
    { id: 'fight', label: 'Fight', text: 'Click a creature to attack it. Keys 1–6 use abilities.' },
    {
      id: 'task',
      label: 'Your first task',
      text: `Talk to ${DEMO_GUIDE_NAME}. She is close by: the gold ◆ on the map.`,
    },
  ],
};

/**
 * The step to show, or null when this browser has already been through it.
 *
 * @param input.seen the step was completed on this browser before ({@link readHowToPlaySeen})
 * @param input.controlsChosen the on-screen-controls setting was ever chosen (`mobileControlsChosen`)
 */
export function howToPlayPlan(input: {
  input: HintInput;
  seen: boolean;
  controlsChosen: boolean;
}): HowToPlayPlan | null {
  if (input.seen) return null;
  return {
    input: input.input,
    title: 'How to play',
    rows: ROWS[input.input].map((r) => ({ ...r })),
    offerControls: input.input === 'touch' && !input.controlsChosen,
    goLabel: HOW_TO_PLAY_GO,
  };
}

/** The page's localStorage, or null where it is absent or refused. */
export function pageStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** True when this browser has completed the step. Unreadable storage ⇒ false (fail open: it shows). */
export function readHowToPlaySeen(storage: Pick<Storage, 'getItem'> | null | undefined): boolean {
  try {
    return storage?.getItem(HOW_TO_PLAY_KEY) != null;
  } catch {
    return false;
  }
}

/** Record the step as done. Never throws (storage may be refused in private mode). */
export function markHowToPlaySeen(storage: Pick<Storage, 'setItem'> | null | undefined): void {
  try {
    storage?.setItem(HOW_TO_PLAY_KEY, 'done');
  } catch {
    // Refused: it shows once per page load instead.
  }
}
